import { Suspense, lazy, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createBrowserRouter, Navigate, Outlet, useLocation, useParams } from 'react-router-dom';

import { useAuthStore } from '@/stores/auth';
import type { FeatureKey, Role } from '@/api/auth';
import { IS_APP_BUILD } from '@/lib/appBrand';
import { useSessionReady } from '@/hooks/useSessionReady';
import { StoreEntry } from '@/pages/guest/StoreEntry';
import { GuestHomePage } from '@/pages/guest/GuestHomePage';
import { GuestVocabularyPage } from '@/pages/guest/GuestVocabularyPage';
import { closedRedirect } from '@/lib/aiEntries';
import { aiSkillsLoaded } from '@/lib/aiSkills';
import { useAppConfig } from '@/hooks/useAppConfig';
import { aiSkillsWanted, useAiSkills } from '@/hooks/useAiSkills';
import { AiGate } from './AiGate';
import { RequireChat, RequireFeature, RequireTrainers } from './RequireFeature';

// Auth
import { LoginPage } from '@/pages/auth/LoginPage';
import { RegisterPage } from '@/pages/auth/RegisterPage';
import { EnterCodePage, LoginCodePage } from '@/pages/auth/EnterCodePage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';

// Public
import { LandingPage } from '@/pages/public/LandingPage';
import { DeleteAccountPage, PrivacyPolicyPage, TermsPage, SupportPage } from '@/pages/public/legal/LegalPage';
import { CoursePage } from '@/pages/public/CoursePage';

// Shell + 404
import { AppShell } from '@/components/layout/AppShell';
import { RouteErrorScreen } from '@/components/layout/RouteErrorScreen';
import { NotFoundPage } from '@/pages/NotFoundPage';

// Admin
import { AdminDashboardPage } from '@/pages/admin/AdminDashboardPage';
import { AdminMarathonsPage } from '@/pages/admin/AdminMarathonsPage';
import { AdminMarathonDetailPage } from '@/pages/admin/AdminMarathonDetailPage';
import { AdminStudentsPage } from '@/pages/admin/AdminStudentsPage';
import { AdminLeadsPage } from '@/pages/admin/AdminLeadsPage';
import { AdminStudentDetailPage } from '@/pages/admin/AdminStudentDetailPage';
import { AdminTrashPage } from '@/pages/admin/AdminTrashPage';
import { AdminCuratorsPage } from '@/pages/admin/AdminCuratorsPage';
import { AdminAppConfigPage } from '@/pages/admin/AdminAppConfigPage';
import { AdminPlatformHealthPage } from '@/pages/admin/AdminPlatformHealthPage';
import { AdminTrainerPoolPage } from '@/pages/admin/AdminTrainerPoolPage';
import { AdminPreviewPage } from '@/pages/admin/AdminPreviewPage';
import { AdminServerPage } from '@/pages/admin/AdminServerPage';
import { AdminAppCrashesPage } from '@/pages/admin/AdminAppCrashesPage';
import { AdminDevicesPage } from '@/pages/admin/AdminDevicesPage';
import { CuratorTasksPage } from '@/pages/curator/CuratorTasksPage';
import { CuratorTrainerSchedulePage } from '@/pages/curator/CuratorTrainerSchedulePage';
import { AdminGroupsPage } from '@/pages/admin/AdminGroupsPage';
import { AdminHomeworkPage } from '@/pages/admin/AdminHomeworkPage';
import { AdminImportsPage } from '@/pages/admin/AdminImportsPage';
import { AdminStudentImportPage } from '@/pages/admin/AdminStudentImportPage';
import { AdminStatsPage } from '@/pages/admin/AdminStatsPage';
import { AdminRecordingsPage } from '@/pages/admin/AdminRecordingsPage';
import { AdminChatMonitoringPage } from '@/pages/admin/AdminChatMonitoringPage';
import { AdminMaterialsPage } from '@/pages/admin/AdminMaterialsPage';
import { AdminIeltsWritingPage } from '@/pages/admin/AdminIeltsWritingPage';
import { AdminIeltsSpeakingPage } from '@/pages/admin/AdminIeltsSpeakingPage';
import { AdminIeltsReadingPage } from '@/pages/admin/AdminIeltsReadingPage';
import { AdminIeltsListeningPage } from '@/pages/admin/AdminIeltsListeningPage';
import { AdminIeltsPoolPage } from '@/pages/admin/AdminIeltsPoolPage';
import { AdminAiPipelinePage } from '@/pages/admin/AdminAiPipelinePage';
import { AdminNotificationTemplatesPage } from '@/pages/admin/AdminNotificationTemplatesPage';
import { LessonBuilderPage } from '@/pages/admin/LessonBuilder/LessonBuilderPage';

// Student
import { StudentDashboardPage } from '@/pages/student/StudentDashboardPage';
import { StudentLearningHubPage } from '@/pages/student/learning/StudentLearningHubPage';
import { StudentMarathonPage } from '@/pages/student/StudentMarathonPage';
import { StudentLessonPlayerPage } from '@/pages/student/Lesson/StudentLessonPlayerPage';

// Lazy-loaded: the LiveKit SDK is ~500KB gzip and only needed inside a room, so
// keep it out of the main bundle.
const LiveRoomPage = lazy(() =>
  import('@/pages/live/LiveRoomPage').then((m) => ({ default: m.LiveRoomPage })),
);
const liveRoom = (as: 'student' | 'curator') => (
  <Suspense fallback={null}>
    <LiveRoomPage as={as} />
  </Suspense>
);
const SpeakingRoomPage = lazy(() =>
  import('@/pages/live/SpeakingRoomPage').then((m) => ({ default: m.SpeakingRoomPage })),
);
const speakingRoom = (as: 'student' | 'curator') => (
  <Suspense fallback={null}>
    <SpeakingRoomPage as={as} />
  </Suspense>
);
/**
 * Сторис у куратора и админа — конструктор, список, статистика (сторис 2.0, 13.10.2026). Ленивым куском: строки и
 * ручки сторис не должны лежать в основном куске, который получает и бесплатник (договор `stories.md` §1). В
 * приложении 1.1.2 раздел есть (D61 снят) — у куратора на телефоне это главный способ публиковать.
 */
const StaffStoriesPage = lazy(() => import('@/stories/staff/StaffStoriesPage'));
const staffStories = (as: 'admin' | 'curator') => (
  <Suspense fallback={null}>
    <StaffStoriesPage as={as} />
  </Suspense>
);
// Текст оферты — 90 КБ на двух языках. Читают её единицы, а платили бы за неё
// в весе сборки все, поэтому отдельным куском.
import { StudentHomeworkPage } from '@/pages/student/StudentHomeworkPage';
import { StudentProfilePage } from '@/pages/student/StudentProfilePage';
import { MobileSettingsPage } from '@/pages/student/MobileSettingsPage';
import { RecordQueuePage } from '@/pages/student/RecordQueue';
import { StudentMaterialsPage } from '@/pages/student/StudentMaterialsPage';
import { StudentVocabularyPage } from '@/pages/student/StudentVocabularyPage';
import { StudentSchedulePage } from '@/pages/student/StudentSchedulePage';
import { StudentSpeakingRoomsPage } from '@/pages/student/StudentSpeakingRoomsPage';
import { StudentCueCardsPage } from '@/pages/student/StudentCueCardsPage';
import { CueCardsTrainerPage } from '@/pages/student/CueCardsTrainerPage';
import { StudentChatPage } from '@/pages/student/StudentChatPage';
import { StudentSpeakingPage } from '@/pages/student/StudentSpeakingPage';
import { StudentIELTSWritingPage } from '@/pages/student/StudentIELTSWritingPage';
import { StudentIELTSSpeakingPage } from '@/pages/student/StudentIELTSSpeakingPage';
import { StudentIELTSReadingPage } from '@/pages/student/StudentIELTSReadingPage';
import { StudentIELTSDiagnosticPage } from '@/pages/student/StudentIELTSDiagnosticPage';
import { StudentIELTSMockPage } from '@/pages/student/StudentIELTSMockPage';
import { StudentIELTSSprintPage } from '@/pages/student/StudentIELTSSprintPage';
import { VideoRoomPreviewPage } from '@/pages/live/VideoRoomPreviewPage';
import { StudentIELTSListeningPage } from '@/pages/student/StudentIELTSListeningPage';
import { TrainersHubPage } from '@/pages/student/trainers/TrainersHubPage';
import { DrillPage } from '@/pages/student/trainers/DrillPage';
import { IeltsTrainerPage } from '@/pages/student/IeltsTrainerPage';
import { StudentCEFRExamPage } from '@/pages/student/StudentCEFRExamPage';
import { StudentLeaderboardPage } from '@/pages/student/StudentLeaderboardPage';
import { StudentNotificationsPage } from '@/pages/student/StudentNotificationsPage';

// Curator
import { CuratorDashboardPage } from '@/pages/curator/CuratorDashboardPage';
import { CuratorStudentsPage } from '@/pages/curator/CuratorStudentsPage';
import { CuratorStudentDetailPage } from '@/pages/curator/CuratorStudentDetailPage';
import { StudentProgressPage } from '@/pages/curator/StudentProgressPage';
import { StudentLessonReviewPage } from '@/pages/curator/StudentLessonReviewPage';
import { CuratorHomeworkPage } from '@/pages/curator/CuratorHomeworkPage';
import { CuratorFlaggedWorksPage } from '@/pages/curator/CuratorFlaggedWorksPage';
import { CuratorChatPage } from '@/pages/curator/CuratorChatPage';
import { CuratorSchedulePage } from '@/pages/curator/CuratorSchedulePage';
import { CuratorSpeakingRoomsPage } from '@/pages/curator/CuratorSpeakingRoomsPage';
import { CuratorCueCardsPage } from '@/pages/curator/CuratorCueCardsPage';
import { CuratorProfilePage } from '@/pages/curator/CuratorProfilePage';
import { CuratorMaterialsPage } from '@/pages/curator/CuratorMaterialsPage';
import { CuratorNotificationsPage } from '@/pages/curator/CuratorNotificationsPage';
import { CuratorGroupsPage } from '@/pages/curator/CuratorGroupsPage';
import { CuratorGroupsCoursePage } from '@/pages/curator/groups/CuratorGroupsCoursePage';
// «Ещё» сотрудника на телефоне: разделы вне нижнего меню, профиль, язык, «Выйти» (13.10.2026).
import { StaffMorePage } from '@/pages/staff/StaffMorePage';
import { VerifyEmailPage } from '@/pages/auth/VerifyEmailPage';
import { biometricsEnabled, confirmIdentity } from '@/lib/biometrics';
import { loginPathWithNext, markUserLogout } from '@/lib/loginReturn';

/** Куда вести без сеанса: на вход, с возвратом на текущий экран кабинета (техдолг 299). */
function useLoginPath(): string {
  const { pathname, search } = useLocation();
  return loginPathWithNext(pathname + search);
}

function RequireAuth() {
  const isAuthed = useAuthStore((s) => s.isAuthenticated());
  const loginPath = useLoginPath();
  if (!isAuthed) return <Navigate to={loginPath} replace />;
  return <Outlet />;
}

function landingFor(role: Role): string {
  return role === 'admin' || role === 'manager'
    ? '/admin'
    : role === 'curator'
      ? '/curator'
      : '/app';
}

/** Короткая пауза на чтение сессии — без текста, чтобы не мигать словами. */
function SessionLoading() {
  return <div className="min-h-screen bg-background" aria-hidden />;
}

/**
 * Подтверждение личности при запуске, если включён вход по лицу или отпечатку.
 *
 * Флаг подтверждения живёт в памяти, а не в хранилище: он должен сбрасываться
 * при каждом запуске приложения — в этом весь смысл.
 *
 * Отказ не запирает человека снаружи: остаётся кнопка входа по паролю. Это и
 * требование Apple, и здравый смысл — палец может не читаться, а при добавлении
 * нового лица система намеренно обесценивает доступ.
 */
let identityConfirmed = false;

function BiometricGate({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const logout = useAuthStore((s) => s.logout);
  const [ok, setOk] = useState(() => identityConfirmed || !biometricsEnabled());
  const [failed, setFailed] = useState(false);

  const ask = async () => {
    setFailed(false);
    const passed = await confirmIdentity(t('biometrics.reason'), t('biometrics.usePassword'));
    if (passed) {
      identityConfirmed = true;
      setOk(true);
    } else {
      setFailed(true);
    }
  };

  useEffect(() => {
    if (!ok) void ask();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (ok) return <>{children}</>;

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center">
      {/* Была иконка `fingerprint`, которой НЕТ в вырезанном подмножестве шрифта:
          на экране блокировки вместо отпечатка появлялось слово «FINGERPRINT»
          кеглем 3rem во всю ширину. `lock` в подмножестве есть. */}
      <span className="material-symbols-outlined text-5xl text-primary">lock</span>
      <p className="text-on-surface-variant">{t('biometrics.locked')}</p>
      <button
        onClick={() => void ask()}
        className="rounded-button bg-primary px-5 py-2.5 font-semibold text-on-primary"
      >
        {t('biometrics.unlock')}
      </button>
      {failed && (
        <button
          onClick={() => {
            // «Войти паролем» — выход по своей воле: вход без `?next` (lib/loginReturn.ts, 27.09.2026).
            markUserLogout();
            logout();
          }}
          className="text-sm font-semibold text-on-surface-variant underline"
        >
          {t('biometrics.usePassword')}
        </button>
      )}
    </div>
  );
}

function RoleGate({ roles, children }: { roles: Role[]; children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const ready = useSessionReady();
  const loginPath = useLoginPath();
  // Нативное хранилище читается асинхронно: пока сессия не восстановлена, `user`
  // пуст у ВСЕХ, включая вошедших. Без этой паузы приложение выбрасывало бы
  // человека на экран входа при каждом запуске.
  if (!ready) return <SessionLoading />;
  if (!user) return <Navigate to={loginPath} replace />;
  if (!roles.includes(user.role)) {
    return <Navigate to={landingFor(user.role)} replace />;
  }
  return <BiometricGate>{children}</BiometricGate>;
}

/**
 * Financial pages (analytics/stats, payments) are director-only. A `manager`
 * has the staff role and reaches the admin shell, but must never see money
 * results — redirect managers away from these routes. Server-side RBAC is the
 * real guard; this is UX only.
 *
 * Тем же сторожем закрыта корзина удалённых учёток: удалять, восстанавливать и
 * смотреть её может только admin (решение владельца F6, 11.09.2026). Отдельного
 * компонента под неё нет намеренно — логика та же, а каждый лишний компонент в
 * этом файле добавляет предупреждение линтера (react-refresh).
 */
function RequireAdmin({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/admin" replace />;
  return <>{children}</>;
}

// Гейт разделов `RequireFeature` и гейт раздела тренажёров `RequireTrainers` — в `./RequireFeature.tsx`.

// Тренажёр по навыку: возможность выбирается по адресу.
//
// Незнакомый навык уводим на список тренажёров, а не показываем пустой экран.
function IeltsTrainerBySkill() {
  const { skill } = useParams<{ skill: string }>();
  // ⚠️ Закрытый вход проверяется ДО тарифа (D22, 11.09.2026): тренажёр речи
  // спрятан у всех, и ученик с речью в тарифе не должен проходить мимо.
  useAppConfig();
  // Навык закрывает тумблер (docs/CONTRACTS/ai-skills.md); пока ответа нет — ждём,
  // а не уводим на хаб. Ждёт только тот, кому ручка нужна по тарифу.
  const aiUser = useAuthStore((s) => s.user);
  useAiSkills();
  if (aiSkillsWanted(aiUser) && !aiSkillsLoaded()) return null;
  const closed = closedRedirect(`/app/trainers/ielts/${skill}`);
  if (closed) return <Navigate to={closed} replace />;
  const key = SKILL_FEATURE[skill ?? ''];
  if (!key) return <Navigate to="/app/trainers" replace />;
  return (
    <RequireFeature feature={key}>
      <IeltsTrainerPage />
    </RequireFeature>
  );
}

const SKILL_FEATURE: Record<string, FeatureKey> = {
  reading: 'ielts_reading',
  listening: 'ielts_listening',
  writing: 'ielts_writing',
  speaking: 'ielts_speaking',
};

export const router = createBrowserRouter([
  // ----- Public -----
  // ⚠️ В ПРИЛОЖЕНИИ ЛЕНДИНГА И ГОСТЯ НЕТ — ОТСУТСТВИЕМ, А НЕ ФЛАГОМ (26.09.2026, сборка 61, вариант Б):
  // вошедший — в кабинет, не вошедший — на вход (`StoreEntry`). Ветка `IS_APP_BUILD ? … : …` решается при
  // сборке: лендинг с заявкой («Записаться на пробное», `LeadModal`) и гостевые экраны в dist-mobile не
  // попадают вовсе. До 26.09 здесь решал isStoreBuild (во время работы) — код ехал в приложение
  // и прятался. Сайт не меняется. Держит app/guestMode.test.ts и dist-mobile-guard.sh.
  { path: '/', element: IS_APP_BUILD ? <StoreEntry /> : <LandingPage /> },
  // Гостевые экраны — только в сборке сайта (там они уводят на «/»); в приложении гостя нет.
  ...(IS_APP_BUILD
    ? []
    : [
        { path: '/guest', element: <GuestHomePage /> },
        { path: '/guest/vocabulary', element: <GuestVocabularyPage /> },
        // Тест на уровень `/diagnostic` с 26.10.2026 — страница самой diagnostic-service: шлюз
        // отдаёт её мимо сайта (location `^/diagnostic(/|$)`), здесь маршрута нет.
      ]),
  // Публичные документы. Ссылки на них Apple и Google проверяют открытием
  // при подаче приложения, поэтому это страницы, а не файлы на скачивание.
  { path: '/privacy', element: <PrivacyPolicyPage /> },
  // Адрес указывается в кабинете Google Play; открывается без входа.
  { path: '/delete-account', element: <DeleteAccountPage /> },
  { path: '/support', element: <SupportPage /> },
  { path: '/terms', element: <TermsPage /> },
  {
    // ⚠️ ОФЕРТА УБРАНА ИЗ ИНТЕРНЕТА 31.08.2026 — решение заказчика.
    //
    // В ней порядок оплаты, тарифы и платёжные системы. В приложении она была
    // закрыта и раньше (правило Apple 3.1.1 задевает и показ цен), теперь
    // закрыта и на сайте: адрес ведёт на условия использования.
    //
    // ⚠️ С 14.09.2026 страницы и текста оферты в frontend НЕТ (решение владельца,
    // Apple 3.1.3(f)): текст без изменений лежит в docs/archive/offer-text-2026-09-14.ts.
    // Напоминаю: оферта это договор, на который опираются отношения с учеником;
    // последствия её отсутствия юридические, не технические.
    path: '/offer',
    element: <Navigate to="/terms" replace />,
  },

  { path: '/courses/:slug', element: <CoursePage /> },
  // ⚠️ Публичной страницы результата оплаты больше НЕТ (01.09.2026).
  // Онлайн-оплаты в интерфейсе нет с 19.08.2026, а страница оставалась
  // общедоступной — с надписями «Оплата прошла!», «К тарифам» и кнопкой
  // «[dev] Эмулировать успешную оплату». Проверяющий магазина открыл бы её по
  // прямой ссылке и увидел готовый платёжный поток.

  // ----- Auth -----
  { path: '/login', element: <LoginPage /> },
  { path: '/register', element: <RegisterPage /> },
  // Вход по коду из письма и последний шаг регистрации. Маршруты ПУБЛИЧНЫЕ:
  // сессии на этот момент ещё нет — она и выдаётся по коду.
  { path: '/login-code', element: <LoginCodePage /> },
  { path: '/enter-code', element: <EnterCodePage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },

  // ----- Authenticated -----
  {
    element: <RequireAuth />,
    children: [
      // Ввод кода из письма. Внутри RequireAuth: человек уже авторизован —
      // регистрация сразу выдаёт сессию, подтверждение адреса идёт следом.
      { path: '/verify-email', element: <VerifyEmailPage /> },
      // Student shell
      {
        path: '/app',
        element: <RoleGate roles={['student', 'admin']}><AppShell role="student" /></RoleGate>,
        // ⚠️ Свой экран вместо заглушки React Router со стеком (техдолг 263). Стоит у
        // всех трёх кабинетов: ловит и падение самой оболочки, и любой страницы внутри.
        errorElement: <RouteErrorScreen />,
        children: [
          { index: true, element: <StudentDashboardPage /> },
          { path: 'lessons', element: <StudentLearningHubPage /> },
          { path: 'lessons/marathon/:marathonId', element: <StudentMarathonPage /> },
          { path: 'lessons/:lessonId', element: <StudentLessonPlayerPage /> },
          // ⚠️ Хаб и дрилл — под разделом тренажёров (02.10.2026): бесплатник не видит ни заглушки «скоро
          // вернутся», ни самого раздела; сюда же приходят перенаправления закрытых входов (`?closed=…`).
          { path: 'trainers', element: <RequireTrainers><TrainersHubPage /></RequireTrainers> },
          { path: 'trainers/drill', element: <RequireTrainers><DrillPage /></RequireTrainers> },
          // Тренировка IELTS: задания сразу по слабым и неизвестным типам,
          // без выбора теста из списка.
          {
            path: 'trainers/ielts/:skill',
            // ⚠️ Ключ зависит от навыка В АДРЕСЕ, поэтому обычной обёрткой его
            // не задать: чтение и письмо — один и тот же маршрут. Раньше здесь
            // стоял общий `ielts`, и тариф «только чтение и слушание» не
            // открывал ни одного тренажёра.
            element: <IeltsTrainerBySkill />,
          },
          { path: 'homework', element: <StudentHomeworkPage /> },
          { path: 'cue-cards', element: <RequireFeature feature="vocab"><StudentCueCardsPage /></RequireFeature> },
          { path: 'cue-cards/:setId', element: <RequireFeature feature="vocab"><CueCardsTrainerPage /></RequireFeature> },
          { path: 'vocabulary', element: <RequireFeature feature="vocab"><StudentVocabularyPage /></RequireFeature> },
          { path: 'ielts/diagnostic', element: <RequireFeature feature="mock"><StudentIELTSMockPage /></RequireFeature> },
          { path: 'ielts/mock', element: <RequireFeature feature="mock"><StudentIELTSMockPage /></RequireFeature> },
          { path: 'ielts/sprint', element: <AiGate path="/app/ielts/sprint"><RequireFeature feature="sprint"><StudentIELTSSprintPage /></RequireFeature></AiGate> },
          { path: 'ielts/writing', element: <RequireFeature feature="ielts_writing"><StudentIELTSWritingPage /></RequireFeature> },
          { path: 'ielts/speaking', element: <AiGate path="/app/ielts/speaking"><RequireFeature feature="ielts_speaking"><StudentIELTSSpeakingPage /></RequireFeature></AiGate> },
          { path: 'ielts/reading', element: <RequireFeature feature="ielts_reading"><StudentIELTSReadingPage /></RequireFeature> },
          { path: 'ielts/listening', element: <RequireFeature feature="ielts_listening"><StudentIELTSListeningPage /></RequireFeature> },
          /**
           * ⚠️ КОМНАТА И ЖИВОЙ УРОК — И В ПРИЛОЖЕНИИ, ПОД РАЗДЕЛОМ (02.10.2026, решение владельца к 1.1.1: только
           * микрофон, без камеры; вход — из календаря, списка комнат в приложении нет).
           *
           * Нет раздела в `sections` — молча на главную: бесплатник не видит ни комнат, ни уроков. Сервер комнату
           * тарифом не закрывает, поэтому в приложении гейт обязателен; на сайте комната — как была, без гейта.
           * То же правило для кнопки «Войти» в календаре — `lib/callSections.ts`. Сторож — `app/storeAbsent.test.ts`.
           */
          { path: 'live/:lessonId', element: <RequireFeature feature="live">{liveRoom('student')}</RequireFeature> },
          { path: 'speaking/:roomId', element: IS_APP_BUILD ? <RequireFeature feature="speaking_peers">{speakingRoom('student')}</RequireFeature> : speakingRoom('student') },
          /**
           * ⚠️ РАСПИСАНИЕ ОТКРЫТО ВСЕМ, И ЗАМКА ЗДЕСЬ НЕТ — И НА САЙТЕ, И В ПРИЛОЖЕНИИ.
           *
           * Стоял `feature="live"` — то есть весь календарь запирался тем же
           * ключом, что и живые уроки. Заказчик 07.09.2026 прислал снимок:
           * ученица с тарифом видит на «Расписании» замок «Доступно ученикам
           * академии» и спрашивает, при чём тут это.
           *
           * Он прав. Календарь — это СОБСТВЕННЫЕ события человека: задания на
           * день, разговорные комнаты, вебинары, личные заметки. Живой урок —
           * лишь один из видов событий, и отсутствие права на него не повод
           * прятать остальное. Замок остаётся на самой комнате урока (маршрут
           * `live/:lessonId` выше): туда без права входить действительно нельзя.
           *
           * ⚠️ В ПРИЛОЖЕНИИ С 1.1.1 (решение владельца 02.10.2026; с 26.09 по 02.10 маршрут в сборку не входил).
           * Бесплатник видит пустой календарь со своими личными записями; комнаты и живые уроки — только при
           * разделе, одним правилом на событие, кнопку «Войти» и запрос (`lib/calendarAccess.ts`). Вкладки
           * календаря в приложении нет: вход — недельный блок главной. Сторожа — `app/storeAbsent.test.ts`,
           * `pages/student/scheduleLinks.test.ts`.
           */
          { path: 'schedule', element: <StudentSchedulePage /> },
          /**
           * Чат — В ПРИЛОЖЕНИИ С 1.1.1 (решение владельца 02.10.2026; с 26.09 по 02.10 маршрут в сборку не
           * входил). Платнику — по разделу `chat`; бесплатнику раздела нет: маршрут уводит на главную, входов
           * нет, запросов к chat-service нет (`RequireChat` — в приложении строгий). Вкладки чата нет: вход —
           * значок в шапке главной. Сторожа — `app/storeAbsent.test.ts`, `hooks/studentChatRequests.test.ts`.
           */
          { path: 'chat', element: <RequireChat><StudentChatPage /></RequireChat> },
          /**
           * ⚠️ ТОЛЬКО САЙТ: СПИСОК РАЗГОВОРНЫХ КОМНАТ, РАЗГОВОР С ИИ, CEFR (26.09.2026, сборка 61).
           *
           * В приложении этих разделов нет вовсе — отсутствием, а не флагом (меню приложения — только из
           * `lib/storeTabs.ts`). Маршруты в приложении не регистрируются, экраны
           * и их запросы в dist-mobile не попадают. На сайте всё как было. Сторож — `app/storeAbsent.test.ts`.
           */
          ...(IS_APP_BUILD
            ? []
            : [
                { path: 'speaking-rooms', element: <StudentSpeakingRoomsPage /> },
                {
                  path: 'speaking',
                  element: (
                    <RequireFeature feature={['speaking_ai', 'speaking_peers']}>
                      <StudentSpeakingPage />
                    </RequireFeature>
                  ),
                },
                { path: 'ielts/diagnostic-legacy', element: <RequireFeature feature="mock"><StudentIELTSDiagnosticPage /></RequireFeature> },
                { path: 'live-preview', element: <VideoRoomPreviewPage /> },
                { path: 'cefr', element: <RequireFeature feature="cefr"><StudentCEFRExamPage /></RequireFeature> },
              ]),
          { path: 'profile', element: <StudentProfilePage /> },
          // Настройки телефона — отдельным экраном за шестерёнкой, как в макете.
          // На широком экране страница сама уводит обратно в профиль.
          { path: 'profile/settings', element: <MobileSettingsPage /> },
          // Неотправленные записи голоса — экран за строкой в настройках (промпт 22, 18.10.2026). На широком экране
          // тот же список стоит в карточке профиля «Неотправленные работы».
          { path: 'profile/records', element: <RecordQueuePage /> },
          { path: 'materials', element: <StudentMaterialsPage /> },
          {
            // ⚠️ СТРАНИЦЫ ТАРИФОВ У УЧЕНИКА НЕТ НИГДЕ — ни в приложении, ни на
            // сайте (решение владельца 31.08.2026).
            //
            // Раньше она пряталась только в магазинной сборке. Но отказ Apple
            // от 30.08.2026 пришёл со снимком САЙТА: проверяющий заходит туда
            // тем же демо-доступом, и прямая ссылка приводила его к ценам.
            // Тарифами управляет администратор, ученику их видеть незачем.
            path: 'tariffs',
            element: <Navigate to="/app" replace />,
          },
          { path: 'leaderboard', element: <StudentLeaderboardPage /> },
          { path: 'notifications', element: <StudentNotificationsPage /> },
        ],
      },

      // Curator shell
      {
        path: '/curator',
        element: <RoleGate roles={['curator', 'admin']}><AppShell role="curator" /></RoleGate>,
        errorElement: <RouteErrorScreen />,
        children: [
          { index: true, element: <CuratorDashboardPage /> },
          { path: 'students', element: <CuratorStudentsPage /> },
          { path: 'students/:userId', element: <CuratorStudentDetailPage /> },
          // Разбор прогресса ученика: список его уроков и ответы внутри урока.
          // Открывается из карточки ученика, поэтому пункта в меню нет.
          { path: 'students/:userId/progress', element: <StudentProgressPage /> },
          { path: 'students/:userId/lessons/:lessonId', element: <StudentLessonReviewPage /> },
          { path: 'homework', element: <CuratorHomeworkPage /> },
          // Лента уведомлений куратора (11.09.2026): сдачи домашки учеников.
          // Пункт меню с цифрой — 4-й, см. AppShell.
          { path: 'notifications', element: <CuratorNotificationsPage /> },
          // Группы по подтипам (D40): курсы подтипов куратора и работа с
          // учениками курса. Второй путь открывается из карточки курса и из
          // ленты «Ученик переведён».
          { path: 'groups', element: <CuratorGroupsPage /> },
          { path: 'groups/:marathonId', element: <CuratorGroupsCoursePage /> },
          { path: 'flagged-works', element: <CuratorFlaggedWorksPage /> },
          { path: 'chat', element: <CuratorChatPage /> },
          { path: 'schedule', element: <CuratorSchedulePage /> },
          { path: 'live/:lessonId', element: liveRoom('curator') },
          { path: 'speaking-rooms', element: <CuratorSpeakingRoomsPage /> },
          // Задания для списка дел учеников этого куратора.
          { path: 'daily-tasks', element: <CuratorTasksPage /> },
          // Когда открывать тренажёры: дни недели и окно пробного экзамена.
          { path: 'trainer-schedule', element: <CuratorTrainerSchedulePage /> },
          { path: 'speaking/:roomId', element: speakingRoom('curator') },
          { path: 'cue-cards', element: <CuratorCueCardsPage /> },
          // Истории куратора — та же страница, что у админа, но со своими ручками. С 1.1.2 — и в приложении.
          { path: 'stories', element: staffStories('curator') },
          { path: 'live-preview', element: <VideoRoomPreviewPage /> },
          { path: 'materials', element: <CuratorMaterialsPage /> },
          { path: 'profile', element: <CuratorProfilePage /> },
          { path: 'more', element: <StaffMorePage role="curator" /> },
        ],
      },

      // Admin shell
      {
        path: '/admin',
        element: <RoleGate roles={['admin', 'manager']}><AppShell role="admin" /></RoleGate>,
        errorElement: <RouteErrorScreen />,
        children: [
          { index: true, element: <AdminDashboardPage /> },
          { path: 'marathons', element: <AdminMarathonsPage /> },
          { path: 'marathons/:marathonId', element: <AdminMarathonDetailPage /> },
          { path: 'lessons/:lessonId/builder', element: <LessonBuilderPage /> },
          { path: 'students', element: <AdminStudentsPage /> },
          // Массовые загрузки: отчёт и таблица с паролями.
          { path: 'imports', element: <AdminImportsPage /> },
          // Загрузка учеников файлом CSV (D56). Только admin: manager получает 403 от сервера.
          { path: 'imports/new', element: <RequireAdmin><AdminStudentImportPage /></RequireAdmin> },
          { path: 'leads', element: <AdminLeadsPage /> },
          { path: 'students/:userId', element: <AdminStudentDetailPage /> },
          // Корзина удалённых учёток: кто, когда, почему; восстановление.
          { path: 'trash', element: <RequireAdmin><AdminTrashPage /></RequireAdmin> },
          // Те же два экрана, что у куратора: одна страница на оба раздела.
          { path: 'students/:userId/progress', element: <StudentProgressPage /> },
          { path: 'students/:userId/lessons/:lessonId', element: <StudentLessonReviewPage /> },
          { path: 'curators', element: <AdminCuratorsPage /> },
          // Управление приложением: тумблеры возможностей вместо новой сборки.
          { path: 'app-config', element: <AdminAppConfigPage /> },
          { path: 'platform-health', element: <AdminPlatformHealthPage /> },
          { path: 'trainer-pool', element: <AdminTrainerPoolPage /> },
          { path: 'server', element: <AdminServerPage /> },
          // Падения приложения (T23): отчёты из приложения 1.1.2+, анонимные.
          { path: 'app-crashes', element: <AdminAppCrashesPage /> },
          // Витрина правок интерфейса: посмотреть вживую до выката на учеников.
          { path: 'preview', element: <AdminPreviewPage /> },
          // Группы: раньше жили только внутри страницы курса, и найти их было нельзя.
          { path: 'groups', element: <AdminGroupsPage /> },
          // Учёт устройств: у кого доступ на скольких телефонах.
          { path: 'devices', element: <AdminDevicesPage /> },
          // Задания для списка дел ученика.
          { path: 'daily-tasks', element: <CuratorTasksPage /> },
          { path: 'trainer-schedule', element: <CuratorTrainerSchedulePage /> },
          { path: 'schedule', element: <CuratorSchedulePage /> },
          { path: 'live/:lessonId', element: liveRoom('curator') },
          { path: 'speaking-rooms', element: <CuratorSpeakingRoomsPage /> },
          { path: 'speaking/:roomId', element: speakingRoom('curator') },
          { path: 'cue-cards', element: <CuratorCueCardsPage /> },
          { path: 'live-preview', element: <VideoRoomPreviewPage /> },
          { path: 'homework', element: <AdminHomeworkPage /> },
          { path: 'flagged-works', element: <CuratorFlaggedWorksPage /> },
          { path: 'stories', element: staffStories('admin') },
          { path: 'materials', element: <AdminMaterialsPage /> },
          { path: 'ielts-writing', element: <AdminIeltsWritingPage /> },
          { path: 'ielts-speaking', element: <AdminIeltsSpeakingPage /> },
          { path: 'ielts-reading', element: <AdminIeltsReadingPage /> },
          { path: 'ielts-listening', element: <AdminIeltsListeningPage /> },
          { path: 'ielts-pool', element: <AdminIeltsPoolPage /> },
          // Движок ИИ-заданий (D27): генерация, очередь одобрения, промпты. Только admin.
          { path: 'ai-pipeline', element: <RequireAdmin><AdminAiPipelinePage /></RequireAdmin> },
          // Тексты уведомлений в базе (D45): матрица «событие × язык», правка,
          // галочка «опубликовать», история, предпросмотр. Только admin — текст
          // уходит полутора тысячам человек разом, и правка видна сразу.
          {
            path: 'notification-templates',
            element: <RequireAdmin><AdminNotificationTemplatesPage /></RequireAdmin>,
          },
          { path: 'stats', element: <RequireAdmin><AdminStatsPage /></RequireAdmin> },
          { path: 'recordings', element: <AdminRecordingsPage /> },
          // Раздела «Тарифы» в админке нет ни на сайте, ни в приложении (13.10.2026, решение владельца): страница,
          // окно и пункт меню удалены; тарифы в базе и права по ним не тронуты, тариф ученика — в его карточке.
          { path: 'chats', element: <AdminChatMonitoringPage /> },
          { path: 'more', element: <StaffMorePage role="admin" /> },
        ],
      },
    ],
  },

  // ----- 404 -----
  { path: '*', element: <NotFoundPage /> },
]);
